import React, {useEffect, useMemo} from 'react';
import type {FCWithChildren} from 'types';
import {MasterLocale} from 'localization/types';
import type {
  CodeListId,
  CodeListsState,
  CombinedCodeListId,
  LocalCodeListId,
} from 'services/codelists/types';
import {CombinedCodeListIds, PartCodeLists} from 'services/codelists/types';
import {useLocalization} from 'localization';
import type {LoadStatus} from 'api';
import {isLinkedRoleCodeListId} from 'schemaDefinition';
import type {LinkedRoleCodeListId} from 'schemaDefinition/types/linkTypes';
import {AllLocalCodeListIds} from 'schemaDefinition/types/linkTypes';
import {useAppDispatch, useAppSelector} from 'store/hooks';
import {SceneError} from 'components/scene/SceneError';
import {SceneLoader} from 'components/scene/SceneLoader';
import {combineCodelists, selectCodelists} from '../codelistsSlice';
import {useLoadCodelist} from '../hooks/useLoadCodelist';

type Props = {
  codeLists: (CodeListId | LinkedRoleCodeListId)[];
};

const getAggregateStatus = (
  codeListIds: (CodeListId | LinkedRoleCodeListId)[],
  state: CodeListsState,
): LoadStatus => {
  return codeListIds.reduce<LoadStatus>((acc, name) => {
    if (
      isLinkedRoleCodeListId(name) ||
      AllLocalCodeListIds.includes(name as LocalCodeListId)
    ) {
      return acc;
    }
    const codeListStatus = state[name]?.status ?? 'NotLoaded';
    return acc === 'Failed' || codeListStatus === 'Failed'
      ? 'Failed'
      : acc === 'Loaded' && codeListStatus === 'Loaded'
        ? 'Loaded'
        : 'Loading';
  }, 'Loaded');
};

const getNotLoaded = (
  codeListIds: (CodeListId | LinkedRoleCodeListId)[],
  state: CodeListsState,
): CodeListId[] => {
  return codeListIds.filter(
    name =>
      !isLinkedRoleCodeListId(name) &&
      (state[name]?.status ?? 'NotLoaded') === 'NotLoaded',
  ) as CodeListId[];
};

const getNotLoadedOrLoading = (
  codeListIds: (CodeListId | LinkedRoleCodeListId)[],
  state: CodeListsState,
): CodeListId[] => {
  return codeListIds.filter(
    name =>
      !isLinkedRoleCodeListId(name) &&
      ((state[name]?.status ?? 'NotLoaded') === 'NotLoaded' ||
        state[name]?.status === 'Loading'),
  ) as CodeListId[];
};

/**
 * Will only render children after code lists are successfully loaded.
 * Shows spinner while loading, and an error message if any fails
 * @param param0
 * @returns
 */
export const CodeListsGate: FCWithChildren<Props> = ({
  codeLists: givenCodeLists,
  children,
}) => {
  const {t} = useLocalization();
  const loadCodeList = useLoadCodelist();

  const {codeLists, combinedCodeLists} = useMemo(() => {
    // Expand any combined code lists to their individual code lists
    const combinedCodeLists = givenCodeLists.filter(id =>
      CombinedCodeListIds.includes(id as CombinedCodeListId),
    ) as CombinedCodeListId[];
    const codeLists = givenCodeLists.filter(
      id => !CombinedCodeListIds.includes(id as CombinedCodeListId),
    ) as CodeListId[];
    combinedCodeLists.forEach(id => {
      const partIds = PartCodeLists[id];
      partIds.forEach(partId => {
        if (!codeLists.includes(partId)) {
          codeLists.push(partId);
        }
      });
    });

    return {
      codeLists,
      combinedCodeLists,
    };
  }, [givenCodeLists]);

  const {locale} = useLocalization();
  const dispatch = useAppDispatch();
  const codeListsState = useAppSelector(selectCodelists);
  const status = useMemo(
    () => getAggregateStatus(givenCodeLists, codeListsState),
    [codeListsState, givenCodeLists],
  );

  // Trigger loading of code lists not yet loaded
  useEffect(() => {
    const notLoaded = getNotLoaded(codeLists, codeListsState);
    notLoaded.forEach(id => {
      loadCodeList(id, locale ?? MasterLocale);
    });
    // On mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger setting combined codelists
  useEffect(() => {
    const loading = getNotLoadedOrLoading(codeLists, codeListsState);
    const notCombined = getNotLoaded(
      combinedCodeLists,
      codeListsState,
    ) as CombinedCodeListId[];
    if (loading.length === 0 && notCombined.length > 0) {
      notCombined.forEach(id => {
        dispatch(
          combineCodelists({combinedId: id, partIds: PartCodeLists[id]}),
        );
      });
    }
  }, [codeLists, codeListsState, combinedCodeLists, dispatch]);

  if (status === 'Failed') {
    return (
      <SceneError error={t('error.codelist.failedToLoad')} applySceneHeight />
    );
  }

  if (status === 'Loading') {
    return (
      <SceneLoader
        loadingType={t('general.entity.codelist')}
        applySceneHeight
      />
    );
  }

  return <>{children}</>;
};
